import { useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DependencyFilterKeys } from '../../../../../../shared/dependenciesV2';
import useFetchDependenciesAction from '../../hooks/useFetchDependencyAction';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import type { SelectOption } from '../../../../../types';
import FilterCardMultiSelect from '../../../../generic/Filter/FilterCardMultiSelect';

/**
 * License filter which allows the user to filter dependencies by license.
 * Uses deeplicenses endpoint to generate the multiselect options.
 */
const LicenseFilter: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const scope = useSelector(DependenciesV2Store.selectors.getScope);
  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const areLicensesLoading = useSelector(DependenciesV2Store.selectors.getAreLicenseAnnotationsLoading);
  const setFilter = useFetchDependenciesAction(DependenciesV2Store.actions.setFilters);

  const licenses = useSelector(DependenciesV2Store.selectors.getLicenses);

  const licenseOptions = !areLicensesLoading ?
      (licenses ?? []).map((license) => ({
        label: license,
        value: license,
      })) : [];

  useEffect(() => {
    if (!scope) {
      return;
    }

    dispatch(DependenciesV2Store.actions.fetchLicenses(scope));
  }, [dispatch, scope]);

  const selectedOptions = !areLicensesLoading ?
      (filters.licenses ?? []).map((option: string) => ({
        label: option,
        value: option,
      })) : [];

  const onChange = (selected: SelectOption[]) => setFilter({
    ...filters,
    [DependencyFilterKeys.Licenses]: selected.map((option) => option.value),
  });

  return (
    <FilterCardMultiSelect
      label="Licenses"
      options={licenseOptions}
      values={selectedOptions}
      onChange={onChange}
      placeholder={areLicensesLoading ? 'Loading...' : 'Select one or more...'}
      isDisabled={areLicensesLoading}
    />
  );
};

export default LicenseFilter;
